import { Input, StyledButton } from "igfd-ui";

import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Autocomplete,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
  styled,
} from "@mui/material";

import { CircularProgressCenter, TermsModal } from "igfd-ui/src/app-index";

import { useSnackbar } from "igfd-commons/src/snackbar";

import { useCreateRestaurant } from "@dashboard/hooks/clients/useRestaurantClient";
import { CountryNameAndCode, useCountries } from "@dashboard/queries/countries";
import {
  type RestaurantReducerDispatch,
  restaurantCreatedAction,
} from "@dashboard/store/actions/restaurantActions";
import type { ReduxState, RestaurantWithPaymentPlan } from "@dashboard/types";

import { GMapAutoComplete } from "../address/GMapsAutoComplete";
import { FoundRestaurant, formatAdditionalInfo } from "../address/gmap-utils";

export const CreateRestaurantForm = ({
  countries,
  onNewRestaurantCreated,
  onClose,
}: {
  countries: CountryNameAndCode[];
  onNewRestaurantCreated: () => void;
  onClose?: () => void;
}) => {
  const dispatch: RestaurantReducerDispatch = useDispatch();
  const [name, setName] = React.useState("");
  const [street, setStreet] = React.useState("");
  const [city, setCity] = React.useState("");
  const [postalCode, setPostalCode] = React.useState("");
  const [country, setCountry] = React.useState<CountryNameAndCode | null>(null);
  const [saveAdditionalInfo, setSaveAdditionalInfo] = React.useState(false);
  const [additionalInfo, setAdditionalInfo] = React.useState<
    FoundRestaurant["additionalInfo"] | null
  >(null);
  const [addressSearchLoading, setAddressSearchLoading] = React.useState(false);

  const [openTerms, setOpenTerms] = React.useState(false);

  // admins don't need to accept terms
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );

  const isValid = useMemo(() => {
    // city, country and name are required
    return name && city && country?.code;
  }, [name, city, country]);

  const snackbar = useSnackbar();
  const { createRestaurant, loading } = useCreateRestaurant({
    onSuccess: (restaurantWithPlan?: RestaurantWithPaymentPlan) => {
      if (!restaurantWithPlan) {
        return;
      }
      onNewRestaurantCreated();
      dispatch(restaurantCreatedAction(restaurantWithPlan));
      snackbar.success("Restaurant created successfully");
    },
    onError: (error) => {
      console.error(error);
      snackbar.error("Something went wrong. Please try again later.");
    },
  });

  const onSubmit = async () => {
    if (isValid) {
      await createRestaurant({
        name,
        address: {
          street,
          city,
          postalCode,
          countryCode: country?.code!,
        },
        additionalInfo: saveAdditionalInfo ? additionalInfo : null,
      });
    }
  };

  const addRestaurantCallback = async () => {
    // call obSubmit directly if user is admin
    if (userIsAdmin) {
      await onSubmit();
    } else {
      setOpenTerms(true);
    }
  };

  const onKeyUpToProceed = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" && isValid) {
      addRestaurantCallback();
    }
  };

  const onSearchSelect = useCallback(
    (restaurant: FoundRestaurant) => {
      setName(restaurant.name);
      const address = restaurant.address;
      setStreet(address.street || "");
      setCity(address.city);
      setPostalCode(address.postalCode || "");
      setCountry(
        countries.find((country) => country.code === address.countryCode) ||
          null
      );
      const additionalInfo = restaurant.additionalInfo;
      if (!additionalInfo) {
        setSaveAdditionalInfo(false);
        setAdditionalInfo(null);
        return;
      }

      setSaveAdditionalInfo(true);
      setAdditionalInfo(additionalInfo);
    },
    [
      countries,
      setName,
      setStreet,
      setCity,
      setPostalCode,
      setCountry,
      setAdditionalInfo,
      setSaveAdditionalInfo,
    ]
  );

  return (
    <BodyContainer>
      <StyledHeader variant={"h2"}>
        Add your restaurant to Ingredifind
      </StyledHeader>
      {userIsAdmin && (
        <>
          <GMapAutoComplete
            onSelect={onSearchSelect}
            restrictions={countries}
            setLoading={setAddressSearchLoading}
          />
          <Divider>or</Divider>
        </>
      )}
      <Stack sx={{ gap: 4 }}>
        <Input
          fullWidth={true}
          id="name"
          label="Restaurant name"
          type="text"
          disabled={addressSearchLoading}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          fullWidth={true}
          id="streetAddress"
          label="Street address"
          type="text"
          disabled={addressSearchLoading}
          value={street}
          onChange={(e) => setStreet(e.target.value)}
        />
        <Input
          fullWidth={true}
          id="city"
          label="City"
          type="text"
          disabled={addressSearchLoading}
          value={city}
          onChange={(e) => setCity(e.target.value)}
        />
        <Input
          fullWidth={true}
          id="postalCode"
          label="Postal code"
          type="text"
          disabled={addressSearchLoading}
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
        />
        <Autocomplete
          id="country"
          options={countries}
          getOptionLabel={(option) => option.name}
          value={country}
          disabled={addressSearchLoading}
          onChange={(event, newValue) => {
            newValue && setCountry(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Country"
              placeholder={"Pick a country"}
            />
          )}
          onKeyUp={onKeyUpToProceed}
        />
      </Stack>
      {userIsAdmin && additionalInfo && (
        <Input
          fullWidth={true}
          id="additionalInfo"
          label={
            <Stack direction={"row"} alignItems={"center"} gap={1}>
              <Typography>Save the following information</Typography>
              <Switch
                checked={saveAdditionalInfo}
                onChange={(e) => setSaveAdditionalInfo(e.target.checked)}
              />
            </Stack>
          }
          type="text"
          multiline={true}
          value={formatAdditionalInfo(additionalInfo)}
          disabled={true} // We don't allow editing of this field
        />
      )}
      <Stack
        padding={"0 32px"}
        gap={1}
        justifyContent={"space-between"}
        width={"100%"}
      >
        <StyledButton
          color={"primary"}
          size={"large"}
          variant={"contained"}
          disabled={!isValid}
          loading={loading}
          label="Add this restaurant"
          onClick={addRestaurantCallback}
          onKeyUp={onKeyUpToProceed}
        />
        {onClose && (
          <StyledButton
            color={"secondary"}
            size={"large"}
            variant={"text"}
            disabled={loading}
            label="Cancel"
            onClick={onClose}
          />
        )}
      </Stack>
      <TermsModal
        type="restaurant"
        open={openTerms}
        onAccept={onSubmit}
        onClose={() => setOpenTerms(false)}
      />
    </BodyContainer>
  );
};

const BodyContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  display: "flex",
  width: 520,
  padding: theme.spacing(4),
  gap: theme.spacing(3),
}));

const StyledHeader = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  display: "flex",
  width: "100%",
  alignItems: "center",
  textAlign: "center",
  flex: 1,
  maxHeight: 180,
}));

export const CreateRestaurant = ({
  onNewRestaurantCreated,
  onClose,
}: {
  onNewRestaurantCreated: () => void;
  onClose?: () => void;
}) => {
  const { data: countries, isLoading: isLoadingCountries } = useCountries();

  if (isLoadingCountries || !countries) {
    return <CircularProgressCenter />;
  }

  return (
    <CreateRestaurantForm
      countries={countries}
      onNewRestaurantCreated={onNewRestaurantCreated}
      onClose={onClose}
    />
  );
};
